import React, {useContext, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {Icon, makeStyles, Tooltip} from "@material-ui/core";
import {useDrag, useDrop} from "react-dnd";
import {ScreenDesignerContextNew} from "../../../../../../../ContextComponents/ScreenDesignerContextNew";
import {AcceptTypes, SourceTypes} from "../../ToolBar/ToolBarUtils/ItemTypes";
import RenderComponents from "../ScreenComponents";
import {getEmptyImage} from "react-dnd-html5-backend";
import {primaryDropColor} from "../ScreenBar";
import {useCurrentResolution} from "../../../../../../../Hooks/useCurrentResolution";
import {useSelector} from "react-redux";
import {getResponsiveStyles} from "../../utils/lib";
import {ContextMenu} from "../ScreenComponents/RenderByType/ContextMenu";

const useStyles = makeStyles(() => ({
    dragger: {
        display: 'none',
        zIndex: 1000
    },
    column: props => ({
        position: 'relative',
        overflow: 'auto',
        '&:hover': {
            border: props.isSelected ? `1px dashed ${primaryDropColor} !important` : '1px dashed #cccccc !important',
        },
    }),
    columnHover: {
        '&:hover': {
            '& $dragger': {
                display: 'block'
            }
        }
    },
    contextPaper: {
        paddingTop: 0,
        paddingBottom: 0
    },
    menuItemRoot: {
        color: '#676767',
        '&:hover': {
            color: '#000000'
        }
    },
    listIconRoot: {
        minWidth: 30
    }
}));
const initialContextState = {
    mouseX: null,
    mouseY: null,
};

const Column = ({column = {}, index, path, parentPath, parentLength, parentIndex}) => {
    const {
        onDropComponent,
        onDeleteComponent,
        onSelectComponent,
        onDropMultipleComponents,
        onMoveComponent,
        onDuplicateComponent,
        selectedComponent,
        currentCustomResolution
    } = useContext(ScreenDesignerContextNew);

    const [direction, setDirection] = useState(null);
    const [contextMenu, setContextMenu] = useState(initialContextState);
    const [paramStyles, setParamStyles] = useState({});
    const {appEnv} = useSelector(state => state.app)
    const ref = useRef(null);
    const styles = getResponsiveStyles(column.styles, currentCustomResolution);
    const currentResolution = useCurrentResolution(currentCustomResolution);
    const hasChildren = column.children && column.children.length;
    const isSelected = selectedComponent && selectedComponent.path === path;
    const classes = useStyles({isSelected});

    useEffect(() => {

        const res = generateParamStyles({styles, parentLength, hasChildren, currentResolution, path, appEnv});
        setParamStyles(res)
    }, [styles, path, currentResolution, hasChildren, parentLength, appEnv])




    const [{isOver, isOverCurrent}, drop] = useDrop({
        accept: AcceptTypes.column,
        collect: monitor => ({
            isOver: !!monitor.isOver(),
            isOverCurrent: monitor.isOver({shallow: true}),
        }),
        drop: (item, monitor) => {
            const didDrop = monitor.didDrop();
            if (didDrop) return;

            const leftRightDirection = direction === 'left' ? index : index + 1;

            if (direction === 'left' || direction === 'right') {
                if (item.dragType === 'move') {

                    if (item.from === parentPath) return;

                    onMoveComponent(item.component, item.from, `${parentPath}.children`, leftRightDirection, item.index, index);
                    return
                }
                if (item.dragType === 'multiple') {
                    onDropMultipleComponents(item.component, leftRightDirection, `${path}.children`);
                    return
                }
                onDropComponent(item, leftRightDirection, `${parentPath}.children`);
                return
            }

            //drop or move panel inner panel
            if (direction === 'inner' && item.type === 'panel') {
                if (item.dragType === 'move') {
                    onMoveComponent(item.component, item.from, `${parentPath}.children`, index, item.index, index);
                    return
                }
                if (item.dragType === 'multiple') {
                    onDropMultipleComponents(item.component, leftRightDirection, `${path}.children`);
                    return
                }
                onDropComponent(item, index, `${parentPath}.children`);
                return
            }


            if (direction === 'inner') {
                if (item.dragType === 'move') {
                    onMoveComponent(item.component, item.from, `${path}.children`, index, item.index, parentIndex);
                    return
                }
                if (item.dragType === 'multiple') {
                    onDropMultipleComponents(item.component, leftRightDirection, `${path}.children`);
                    return
                }
                onDropComponent(item, column.children ? column.children.length : 0, `${path}.children`)
            }
        },
        hover: (item, monitor) => {
            if (!ref.current) {
                return
            }
            if (!monitor.isOver({shallow: true})) {
                setDir(null);
                return
            }
            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientX = clientOffset.x - hoverBoundingRect.left;

            if (hoverClientX < hoverMiddleX - 40 && item.type === 'panel') {
                setDir('left');
                return;
            }
            if (hoverClientX > hoverMiddleX + 40 && item.type === 'panel') {
                setDir('right');
                return;
            }

            setDir('inner')
        }
    });
    const [{isDragging}, drag, preview] = useDrag({
        item: {
            component: {...column, type: SourceTypes.panel},
            type: SourceTypes.panel,
            dragType: 'move',
            from: parentLength > 1 ? path : parentPath,
            index: parentIndex
        },
        canDrag: true,
        collect: monitor => ({
            isDragging: monitor.isDragging(),
        })
    });

    const dragStyles = generateDragStyles({isDragging, hasChildren, styles, isSelected});


    useEffect(() => {
        preview(getEmptyImage(), {
            captureDraggingState: true
        })
    }, [preview]);
    useEffect(() => {
        if (!isOver) {
            setDir(null)
        }
    }, [isOverCurrent, isOver]);

    function setDir(dir) {
        if (direction !== dir) {
            setDirection(dir)
        }
    }

    function openContextMenu(e) {
        if (contextMenu.mouseY) {
            setContextMenu(initialContextState);
            return
        }
        e.preventDefault();
        setContextMenu({
            mouseX: e.clientX - 2,
            mouseY: e.clientY - 4,
        })
    }

    function closeContextMenu() {
        setContextMenu(initialContextState)
    }


    drop(ref);
    return (
        <div
            ref={ref}
            //className={`col-xs col-sm col-md col-xl col-lg`}
            onContextMenu={(e) => {
                e.stopPropagation();
                openContextMenu(e)
            }}
            onClick={(e) => {
                e.stopPropagation();
                onSelectComponent(column, path)
            }}
            className={`${classes.column} ${classes.columnHover}`}
            style={{
                ...paramStyles,
                ...dragStyles,
            }}
        >
            <div className={classes.dragger} ref={drag}>
                <DragHandle/>
            </div>
            {hasChildren && !isDragging ? (
                column.children.map((item, index) => {
                    return (
                        <RenderComponents
                            key={`${path}.children.${index}`}
                            type={item.type}
                            item={item}
                            index={index}
                            path={`${path}.children.${index}`}
                            parentPath={path}
                            renderDirection={paramStyles.flexDirection}
                        />
                    )
                })
            ) : (
                <div
                    style={{
                        height: direction === 'inner' ? '10px' : 0,
                        background: primaryDropColor,
                        alignSelf: 'center',
                        width: '100%'
                    }}>

                </div>
            )}
            <ContextMenu
                onDropComponent={onDropComponent}
                contextMenu={contextMenu}
                onClose={closeContextMenu}
                item={column}
                removeItem={onDeleteComponent}
                editItem={onSelectComponent}
                duplicateItem={onDuplicateComponent}
                parentPath={parentPath}
                index={index}
                path={path}
                parentLength={parentLength}
            />
            <DropArea direction={direction} isOverCurrent={isOverCurrent}/>
        </div>
    )
};
Column.propTypes = {
    column: PropTypes.object,
    index: PropTypes.number,
    path: PropTypes.string,
    parentPath: PropTypes.string,
    parentLength: PropTypes.number
};
export default Column

export const DragHandle = () => {
    return (
        <div
            style={{
                borderRadius: '0 0 5px 5px',
                background: '#cccccc',
                opacity: 1,
                display: 'flex',
                justifyContent: 'center',
                position: 'absolute',
                left: '49.5%',
                top: -2,
                cursor: 'move'
            }}
        >
            <Tooltip title={'Move Panel'}>
                <Icon style={{
                    transform: 'rotate(90deg)',
                    width: '0.9rem',
                    height: '0.9rem',
                    fontSize: '1.1rem',
                    display: 'flex',
                    alignItems: 'center',
                    color: primaryDropColor
                }}>
                    drag_indicator
                </Icon>
            </Tooltip>
        </div>

    )
};
const DropArea = ({direction, isOverCurrent}) => {
    return (
        <div
            style={{
                position: 'absolute',
                width: direction !== 'inner' && direction !== null && isOverCurrent ? 5 : 0,
                height: '100%',
                background: primaryDropColor,
                left: direction === 'left' && '0',
                right: direction === 'right' && '0',
                top: 0,
                zIndex: 9999,
            }}
        >
        </div>
    )
};
function generateDragStyles({isDragging, hasChildren, styles, isSelected}) {
    const {flexDirection = 'column', alignItems = 'baseline'} = styles;

    function setBorder() {
        if (isDragging) {
            return '1px dashed #cccccc'
        }
        if (isSelected) {
            return `1px dashed ${primaryDropColor}`
        }
        if (hasChildren) {
            return '1px dashed transparent'
        }
        return '1px dashed #cccccc'
    }

    return {
        display: 'flex',
        border: setBorder(),
        flexDirection: hasChildren ? flexDirection : 'row',
        alignItems: hasChildren ? alignItems : 'baseline'
    }
}

function generateParamStyles({styles, parentLength, hasChildren, currentResolution, appEnv}) {
    const {
        width,
        padding,
        margin,
        flexDirection = "column",
        boxShadow,
        shadowColor = "#000000",
        border = "",
        borderColor = "#cccccc",
        height,
        alignItems = 'baseline',
        justifyContent = 'baseline',
        background, backgroundImage
    } = styles;
    const backgroundImagePublicUrl = backgroundImage ? process.env.NODE_ENV === 'development' ?
        `${process.env.REACT_APP_REMOTE_HOST}${appEnv}${backgroundImage.url}` :
        `/${appEnv}` + backgroundImage.url : '';
    let borderObj = border !== "" ? {
        borderTop: `${border.split(" ")[0] || 0} solid`,
        borderRight: `${border.split(" ")[1] || 0} solid`,
        borderBottom: `${border.split(" ")[2] || 0} solid`,
        borderLeft: `${border.split(" ")[3] || 0} solid`
    } : {};
    let isPercentageHeight = height && height.substr(height.length - 1) === '%';
    let adaptiveWidth = typeof width === 'object' ? width[currentResolution] : width
    return {
        width: width ? adaptiveWidth : `${parentLength ? 100 / parentLength : 100}%`,
        padding,
        margin,
        flexDirection: !hasChildren ? 'row' : flexDirection,
        boxShadow: `${boxShadow} ${shadowColor}`,
        height: isPercentageHeight ? '100%' : height,
        minHeight: height ? 'auto' : 30, //add overflow
        ...borderObj,
        borderColor: borderColor,
        alignItems: alignItems,
        justifyContent: !hasChildren ? "center" : justifyContent,
        background,
        backgroundImage: `url(${backgroundImagePublicUrl})`,
        backgroundSize: 'cover'
    }
}